import { UiAtlas, UiIcon } from "./UiIcon";
import { useState } from "react";
import { useDialogA11y } from "./useDialogA11y";
import { getFxLow, setFxLow } from "../lib/fxPref";
import { worldBus } from "../world/bus";
import { getSoundPref, setSoundPref } from "../lib/soundPref";
import { getTextScale, setTextScale, applyReduceMotion, type TextScale } from "../lib/uiPrefs";
import { track } from "../lib/ga4";
import { resetLocalData } from "../lib/resetLocal";
import { anyStoryHere } from "../lib/storyStorage";
import { familyVoiceRecorded } from "../lib/familyVoice";
import { resetPrompt } from "@greg-chick/shared";
import { loadHero } from "../lib/heroStorage";
import { Link } from "react-router-dom";
import { ROUTES } from "../lib/routes";
import { useKids } from "../lib/uiFeaturesApi";
import { adminConsoleUrl } from "../lib/adminLink";

// 설정 시트(홈 ☰·⚙️ 진입) — 실제 동작하는 환경설정만: 소리·모션 최소화·글자 크기 + 소개·개인정보.
// 값은 기존 소유 모듈(sound.ts·fxPref.ts)과 신규 uiPrefs 를 그대로 씀(붕 뜬 토글 없음, 즉시 반영).

const SCALES: { v: TextScale; label: string }[] = [
  { v: 0, label: "보통" },
  { v: 1, label: "크게" },
  { v: 2, label: "더 크게" },
];

function SettingsSheetInner({ onClose }: { onClose: () => void }) {
  const ref = useDialogA11y<HTMLDivElement>(onClose);
  /** 지금 아이 화면인가 — 설정 줄의 말이 뒤집힌다(눌렀을 때 무엇이 달라지는지). */
  const kids = useKids();
  const [sound, setSound] = useState(getSoundPref());
  const [calm, setCalm] = useState(getFxLow());
  const [scale, setScale] = useState<TextScale>(getTextScale());
  // 주소가 안 채워졌으면 undefined → 버튼을 안 그린다
  const adminUrl = adminConsoleUrl();

  function toggleSound() {
    const on = !sound;
    setSound(on);
    setSoundPref(on); // 저장(체험존 입장 시 반영). Howler 비의존.
    // 월드가 떠 있으면 지금 흐르는 소리도 즉시 켜고 끈다 — 저장만 하면 계속 재생된다.
    // (모션과 같은 방식: 여기서 Howler 를 직접 부르면 랜딩 번들에 딸려 들어간다)
    worldBus.emit("sound", { on });
    track({ name: "set_pref", params: { pref: "sound", on } });
  }
  function toggleCalm() {
    const on = !calm;
    setCalm(on);
    setFxLow(on);
    applyReduceMotion(); // CSS 애니메이션 즉시 반영
    // 캔버스(Phaser)는 CSS가 안 닿는다 — 월드가 떠 있으면 즉시 반영시킨다.
    // (월드 HUD의 중복 토글을 없앴으므로 이 설정이 유일한 조작점)
    worldBus.emit("fx", { low: on });
    track({ name: "set_pref", params: { pref: "reduce_motion", on } });
  }
  function pickScale(v: TextScale) {
    setScale(v);
    setTextScale(v); // 즉시 반영(applyTextScale)
  }

  return (
    <div
      ref={ref}
      className="sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="설정"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">⚙️ 설정</h2>
          <button type="button" className="sheet__close" onClick={onClose} aria-label="닫기">
            <UiIcon name="close" size={30} />
          </button>
        </div>

        <div className="sheet__body">
          {/* 소리 */}
          <button type="button" className="setrow" onClick={toggleSound} aria-pressed={sound}>
            <UiIcon name={sound ? "sound_on" : "sound_off"} size={32} className="setrow__ic" />
            <span className="setrow__txt">
              <span className="setrow__label">소리</span>
              <span className="setrow__sub">체험존의 배경음·효과음 {sound ? "켜짐" : "꺼짐"}</span>
            </span>
            <span className={`toggle${sound ? " toggle--on" : ""}`} aria-hidden="true"><i /></span>
          </button>

          {/* 모션 최소화 */}
          <button type="button" className="setrow" onClick={toggleCalm} aria-pressed={calm}>
            <UiIcon name="leaf" size={32} className="setrow__ic" />
            <span className="setrow__txt">
              <span className="setrow__label">움직임 줄이기</span>
              <span className="setrow__sub">화면 움직임을 차분하게 (눈이 편해요)</span>
            </span>
            <span className={`toggle${calm ? " toggle--on" : ""}`} aria-hidden="true"><i /></span>
          </button>

          {/* 글자 크기 */}
          <div className="setrow setrow--static">
            <span className="setrow__ic" aria-hidden="true">🔠</span>
            <span className="setrow__txt">
              <span className="setrow__label">글자 크기</span>
              <span className="setrow__sub">읽기 편한 크기로</span>
            </span>
          </div>
          <div className="seg seg--light" role="group" aria-label="글자 크기">
            {SCALES.map((s) => (
              <button
                key={s.v}
                type="button"
                className="seg__btn"
                aria-pressed={scale === s.v}
                onClick={() => pickScale(s.v)}
              >
                {s.label}
              </button>
            ))}
          </div>

          <hr className="sheet__sep" />

          {/* 소개 — 자세한 이야기는 보호자 안내 한 장으로. 여기 다 적으면 설정이 읽기 화면이 된다 */}
          <section className="sheet__info">
            <h3>소개</h3>
            <p>《그레그와 병아리의 경주 그림책방》은 그림책을 읽고, 체험 공간을 거닐고, 굿즈를 구경하는 아이·보호자용 그림책 놀이터예요.</p>
            <Link className="setrow" to={ROUTES.guide} onClick={onClose}>
              <UiIcon name="guide" size={28} className="setrow__ic" />
              <span className="setrow__label">보호자 안내 자세히 보기</span>
            </Link>
            {/**
             * 🔴 **일반 모드에는 보호자 화면으로 가는 길이 여기밖에 없다**(2026-09-22).
             *
             * 하단의 [🔒 보호자] 단추는 **영유아 모드에서만** 나온다. 그러니 기본 화면을 쓰는
             * 보호자는 「이 기기를 아이 화면으로」 스위치를 **영영 못 찾는다** — 주소를 직접
             * 치지 않는 한. 만들어 놓고 닿을 길이 없는 것과 같다.
             *
             * 🔴 **스위치는 여전히 한 곳뿐이다.** 여기 있는 것은 **문**이고, 고르는 자리는
             * `/family` 안이다(어른 확인 뒤). 같은 스위치를 두 군데 두면
             * 「설정에서 바꿨는데 안 바뀐다」가 생긴다.
             *
             * 🔴 **말은 「지금 누르면 무엇이 되는지」로 적는다** — 화면에 따라 뒤집는다.
             * 「보호자 화면」이라고만 적으면 눌렀을 때 무엇이 달라지는지 알 수 없다.
             */}
            <Link className="setrow" to={ROUTES.family} onClick={onClose}>
              {kids ? (
                <UiIcon group="nav" name="family" size={28} className="setrow__ic" />
              ) : (
                <UiIcon name="teddy" size={28} className="setrow__ic" />
              )}
              <span className="setrow__label">{kids ? "보호자 화면 바꾸기" : "아이 화면 바꾸기"}</span>
            </Link>
          </section>

          {/* 처음부터 다시 — 가입이 없어서 지울 방법이 이것뿐이다 */}
          <section className="sheet__info">
            <h3>처음부터 다시 하기</h3>
            <p>
              이 기기에 저장된 <b>내 히어로·모은 별·랠리 완주</b>를 지우고 처음처럼 시작해요.
              가입이 없어서 전부 이 기기 안에만 있어요 — 지우면 되돌릴 수 없어요.
            </p>
            <button
              type="button"
              className="setrow setrow--danger"
              onClick={() => {
                /**
                 * 🔴 **무엇이 지워지는지 다 말한다.**
                 *
                 * 예전 문구는 「내 히어로·모은 별·랠리 완주」였는데, 실제로는 **그림책과 코드**도
                 * 지워진다. 코드를 잃으면 서버에 있는 그림책·도장으로 **돌아갈 길이 없다** —
                 * 가입이 없어서 코드가 유일한 열쇠다. 학교 태블릿에서 아이가 눌러 볼 수 있는
                 * 자리라 더 그렇다. 넘기기(`handoffPrompt`)와 같은 무게로 묻는다.
                 *
                 * 🔴 코드는 **스토어가 아니라 저장소에서** 읽는다. 스토어는 누군가
                 * `hydrate()` 를 부른 뒤에야 채워지는데, 그 순서에 기대면 「코드가 없어요」라고
                 * 말하고 지워 버릴 수 있다 — 랠리에서 똑같은 경합으로 여권이 바뀌었다.
                 * 게다가 지금 지우려는 것이 바로 그 저장소라, 물어볼 곳도 거기가 맞다.
                 */
                if (
                  !window.confirm(
                    resetPrompt({ code: loadHero()?.code, hasStory: anyStoryHere(), hasVoice: familyVoiceRecorded() }),
                  )
                )
                  return;
                /**
                 * 🔴 **못 지웠으면 그렇다고 말한다.**
                 *
                 * 사생활 보호 모드처럼 저장소를 못 쓰는 기기가 있다. 예전엔 결과를 안 보고
                 * 그냥 첫 화면으로 보냈다 — 보호자는 눌렀는데 **그대로인 화면**을 다시 보고
                 * 고장인 줄 안다. 무엇을 해야 하는지까지 알려 준다.
                 */
                if (resetLocalData().failed) {
                  window.alert(
                    "이 기기에서는 지울 수 없었어요. 브라우저 설정에서 이 사이트의 저장된 데이터를 지워 주세요.",
                  );
                  return;
                }
                // 지운 뒤에는 **새로 고쳐야** 화면이 처음 상태로 돌아온다
                // (히어로·별은 화면이 뜰 때 한 번 읽는다)
                window.location.href = "/";
              }}
            >
              <UiIcon name="reset" size={28} className="setrow__ic" />
              <span className="setrow__label">처음부터 다시 하기</span>
            </button>
          </section>

          {/* 개인정보 — 요약 한 문단 + 전문으로 가는 길(조치계획서 ■10) */}
          <section className="sheet__info">
            <h3>개인정보</h3>
            <p>가입·로그인이 없어요. 이름·이메일·전화 같은 개인정보를 서버로 보내지 않아요. 굿즈 배송 등 꼭 필요한 정보는 외부 스토어 폼에서만 입력해요.</p>
            <Link className="sheet__link" to={ROUTES.privacy} onClick={onClose}>
              <UiIcon name="privacy" size={22} /> 개인정보처리방침 전문 보기
            </Link>
          </section>

          {/*
            운영자 전용 문. 주소가 안 채워졌으면 아예 안 그린다(adminLink 참조) —
            로컬 주소 버튼을 아이 폰에 보여 주면 "눌렀는데 안 되네"가 된다.
            들어가도 토큰을 넣어야 열리므로 이 버튼 자체는 권한을 주지 않는다.
            GA4 로 세지 않는다 — 운영자 행동은 KPI 가 아니고 이벤트 이름만 늘어난다.
          */}
          {adminUrl ? (
            <a className="sheet__admin" href={adminUrl} target="_blank" rel="noopener noreferrer">
              <UiIcon name="tools" size={22} /> 운영자 화면 열기
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * 🧩 이 창은 첫 화면 **위에** 열린다 — 아이콘은 첫 화면 묶음이 아니라 **열릴 때만 받는 제 묶음**으로
 * (조치계획서 ■2: 첫 화면 용량 0.68MB). UiAtlas 는 겹쳐 쓸 수 있고 안쪽이 이긴다.
 */
export function SettingsSheet(props: { onClose: () => void }) {
  return (
    <UiAtlas bundle="settings">
      <SettingsSheetInner {...props} />
    </UiAtlas>
  );
}
